<template>
    <div class="phone-verification-status">
        <h1 class="phone-verification-status__title">
            {{ $t('Verification Status') }}
        </h1>
        <div class="phone-verification__content">
            <div v-if="status !== 'success' && !same">
                <i class="phone-verification-status__error fas fa-times-circle" />
                <p class="phone-verification-status__description">
                    {{ $t(`Sorry, we couldn't verify the number`) }} <strong>{{ getPhone() }}</strong>.
                </p>
            </div>
            <div v-else>
                <p class="phone-verification-status__description">
                    {{ $t(`To keep your current registered number `)}} <strong> {{ getPhone() }}</strong> <br>
                    {{ $t(`active, we need to verify it.`)}} <br>
                </p>
            </div>
            <form class="phone-verification-status__form" @submit.prevent="handleSubmit">
                    <ui-button :loading="loading" class="w-100 mb-1" variant="primary" @click="handleSubmit">
                        {{ $t('Scan the QR code again.') }}
                    </ui-button>
                     <p class="phone-verification-status__update-phone text-muted">
                        {{ $t('Is your number wrong?') }}
                        <button :disabled="tooManyRequests" class="phone-verification-status__link btn btn-link p-0" @click="$refs.phoneNumberModal.show()">
                            {{ $t('Change It') }}
                        </button>
                    </p>
                    <div>
                        <ui-button variant="secondary" @click="redirectToPhoneVerification">
                            <span class="phone-verification-status__other-method-button-text">
                                {{ $t('Try with another method') }}
                            </span>
                            <span class="phone-verification-status__other-method-arrow fas fa-arrow-right"></span>
                        </ui-button>
                    </div>
            </form>
            <vue-recaptcha
            class="w-100"
            ref="recaptchaComponent"
            size="invisible"
            loadRecaptchaScript
            :sitekey="CONST.CAPTCHACODE"
            @verify="OTPInverse"
            />
            <phone-number-modal ref="phoneNumberModal" :country="user.country" :updating="!!user.phone" @phoneUpdated="phoneUpdated" />
        </div>
    </div>
</template>

<script>
import parsePhoneNumber from 'libphonenumber-js'
import PhoneNumberModal from '@/components/phone-verification/PhoneNumberModal.vue';
import UiButton from '@/components/ui/buttons/Button.vue';
import VueRecaptcha from 'vue-recaptcha';
import recaptchaUtils from '@/utils/recaptcha.utils';


export default {
    name: 'PhoneVerificationStatus',
    components: {
        UiButton,
        PhoneNumberModal,
        VueRecaptcha,
    },
    props: {
        status: {
            type: String,
            required: true,
        },
        site_id: {
            type: String,
            required: false,
        },
        phone: {
            type: String,
            required: false,
        },
        same: {
            type: Boolean,
            required: false,
        },
        country: {
            type: String,
            required: false,
        },
        redirect_url: {
            type: String,
            required: false,
        }
    },
    data() {
        return {
            user: null,
            loadingAccount: false,
            redirecting: false,
            loading: false,
            timeInterval: null,
            timeRemaining: 0,
            tooManyRequests: false,
            waitTime: 10,
        };
    },
    mounted() {
        this.getAccountDetails();
    },
    methods: {
        async getAccountDetails() {
            try {
                this.loadingAccount = true;

                const { data } = await this.axiosAccount.get('/api/accounts/me');

                this.user = data;
            } catch(error) {
                if (error?.response?.status === 422) {
                    this.$refs.registerPhoneModal.show();
                    return;
                }

                if (error?.response?.status === 404) {
                    this.redirectToLogin();
                    return;
                }

                this.showError(error);
            } finally {
                this.loadingAccount = false;
            }
        },
        getPhone() {
            const formattedPhoneNumber = parsePhoneNumber(this.phone, this.country);
            return formattedPhoneNumber.formatInternational();
        },
        async handleSubmit() {
            if (!recaptchaUtils.isAvailable()) {
                this.OTPInverse('');
                return;
            }

            this.$refs.recaptchaComponent.reset();
            this.$refs.recaptchaComponent.execute();
        },
        async OTPInverse(token) {
            try {
                this.loading = true;
                const payload = {
                    type: 'otp-inverse',
                    action: 'phone_verification',
                    from: 'Tendencys',
                    complements: {
                        site_id: this.site_id,
                        redirect_url: this.redirect_url,
                    },
                    token,
                };
                const { data } = await this.axiosAccount.post('/api/otp-inverse', payload);
                const { redirectionUrl } = data;
                if (redirectionUrl) {
                    redirectionUrl.startsWith('/') ? this.$router.push(redirectionUrl) : window.location.replace(redirectionUrl);
                }

            } catch (error) {
                if (error?.response?.status === 429) {
                    this.tooManyRequests = true;
                    return;
                }

                if (error?.response?.status === 404) {
                    this.redirectToLogin();
                    return;
                }

                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        phoneUpdated(userData) {
            this.tooManyRequests = false;
            this.user = userData;
        },
        redirectToPhoneVerification() {
            return this.$router.push('/phone-verification?site_id=' + this.site_id + '&redirect_url=' + this.redirect_url);
        },
    }
};
</script>

<style lang="scss">
    .phone-verification-status {
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: center;

        &__content {
            align-items: center;
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-top: 24px;
            max-width: 100%;
            width: 450px;
        }
        
        &__icon {
            font-size: 3rem;
            color: #4CAF50;
        }

        &__title {
            font-size: 2.25rem;
            font-weight: 700;
            margin-bottom: 20px;
        }

        &__subtitle {
            font-size: 1.5rem;
            font-weight: 550;
            margin-bottom: 20px;
        }

        &__description {
            font-size: 1rem;
            margin-top: 1rem;
        }

        &__error {
            font-size: 3rem;
            color: #FF0000;
        }
        
        &__update-phone {
            color: #9093A1;
            font-size: 16px;
            font-weight: 400;
            margin-bottom: 24px;
        }
        
        &__link {
            color: $accounts-secondary !important;
            font-weight: 600 !important;
            font-size: 16px !important;
            text-decoration: none;
        }

        &__other-method-button {
            align-items: center !important;
            display: flex !important;
            height: 46px;
            padding-left: 8px !important;
            padding-right: 8px !important;
            margin-bottom: 0 !important;
            max-width: 100%;
            width: 100%;

            @media(min-width: 768px) {
                width: auto;
            }
        }

        &__other-method-button-text {
            line-height: 130%;
            overflow: hidden;
            text-overflow: ellipsis;
            width: auto;
            white-space: nowrap;
        }

        &__other-method-arrow {
            font-size: 14px;
            margin-left: auto;
            margin-top: -2px;
        }

        &__form {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 24px;
            max-width: 100%;
            width: 450px;
        }

        &__wait-message {
        color: $general-black;
        font-size: 0.9rem;
        font-weight: 400;
        margin-bottom: 15px;
        max-width: 400px;
        background-color: $general-white;
        border-radius: 8px;
        padding: 10px;

        strong {
            font-weight: 800;
        }
    }
    }
</style>